import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import cryptoPayoutsApi from 'services/cryptoPayouts';
import pwcCustomersApi from 'services/pwcCustomers';
import pwcOrdersApi from 'services/pwcOrders';

import accountsApi from '../services/accounts';
import apiKeysApi from '../services/apiKeys';
import assetsApi from '../services/assets';
import customersApi from '../services/customers';
import eventDestinationsApi from '../services/eventDestinations';
import merchantsApi from '../services/merchants';
import ordersApi from '../services/orders';
import payoutsApi from '../services/payouts';
import profileApi from '../services/profile';
import reconciliationsApi from '../services/reconciliations';
import pwcRefundsApi from '../services/refunds';
import staticAssetsApi from '../services/staticAssets';
import usersApi from '../services/users';
import accountSlice from './slices/accountSlice';
import customerSlice from './slices/customerSlice';
import merchantSlice from './slices/merchantSlice';
import notificationsSlice from './slices/notificationsSlice';
import profileSlice from './slices/profileSlice';
import userSlice from './slices/userSlice';

export const rootReducer = combineReducers({
  user: userSlice,
  account: accountSlice,
  merchant: merchantSlice,
  profile: profileSlice,
  notifications: notificationsSlice,
  customer: customerSlice,
  [apiKeysApi.reducerPath]: apiKeysApi.reducer,
  [eventDestinationsApi.reducerPath]: eventDestinationsApi.reducer,
  [ordersApi.reducerPath]: ordersApi.reducer,
  [pwcRefundsApi.reducerPath]: pwcRefundsApi.reducer,
  [pwcOrdersApi.reducerPath]: pwcOrdersApi.reducer,
  [pwcCustomersApi.reducerPath]: pwcCustomersApi.reducer,
  [accountsApi.reducerPath]: accountsApi.reducer,
  [merchantsApi.reducerPath]: merchantsApi.reducer,
  [payoutsApi.reducerPath]: payoutsApi.reducer,
  [cryptoPayoutsApi.reducerPath]: cryptoPayoutsApi.reducer,
  [profileApi.reducerPath]: profileApi.reducer,
  [usersApi.reducerPath]: usersApi.reducer,
  [assetsApi.reducerPath]: assetsApi.reducer,
  [customersApi.reducerPath]: customersApi.reducer,
  [staticAssetsApi.reducerPath]: staticAssetsApi.reducer,
  [reconciliationsApi.reducerPath]: reconciliationsApi.reducer,
});

export const setupStore = (preloadedState?: Partial<RootState>) =>
  configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(
        apiKeysApi.middleware,
        eventDestinationsApi.middleware,
        ordersApi.middleware,
        pwcOrdersApi.middleware,
        pwcRefundsApi.middleware,
        pwcCustomersApi.middleware,
        accountsApi.middleware,
        merchantsApi.middleware,
        payoutsApi.middleware,
        cryptoPayoutsApi.middleware,
        profileApi.middleware,
        usersApi.middleware,
        assetsApi.middleware,
        customersApi.middleware,
        staticAssetsApi.middleware,
        reconciliationsApi.middleware,
      ),
  });

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore['dispatch'];

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
